import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { getStartOf, stringifyDateToISO8601 } from '../../util/dates';
import { parse } from '../../util/urlHelpers';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import { diaryWeekStart } from './InstructorDiaryPage.helpers';

// Lessons in one week fit in one page: 100 is the Marketplace API maximum.
const SALES_PAGE_SIZE = 100;

// Used only when the instructor has no listing with weekly hours yet.
export const FALLBACK_TIME_ZONE = 'Australia/Melbourne';

const TIME_PLAN = 'availability-plan/time';

const entityRefs = entities => entities.map(entity => ({ id: entity.id, type: entity.type }));

/**
 * The listing whose weekly hours the diary shows: the first published one with hourly
 * availability, else any with hourly availability. Instructors normally have one.
 */
export const diaryListing = listings => {
  const timeBased = listings.filter(l => l.attributes?.availabilityPlan?.type === TIME_PLAN);
  return timeBased.find(l => l.attributes?.state === 'published') || timeBased[0] || null;
};

const instructorDiaryPageSlice = createSlice({
  name: 'InstructorDiaryPage',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    weekStartId: null,
    timeZone: FALLBACK_TIME_ZONE,
    ownListings: [],
    exceptions: [],
    transactionRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const { weekStartId, timeZone, ownListings, exceptions, transactions } = action.payload;
        state.fetchInProgress = false;
        state.weekStartId = weekStartId;
        state.timeZone = timeZone;
        state.ownListings = ownListings;
        state.exceptions = exceptions;
        state.transactionRefs = entityRefs(transactions);
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default instructorDiaryPageSlice.reducer;

// ================ Load data ================ //

// Every availability exception overlapping the week, all pages.
const queryAllExceptions = (sdk, params) =>
  sdk.availabilityExceptions.query(params).then(response => {
    const first = response.data.data;
    const totalPages = response.data.meta?.totalPages || 1;
    if (totalPages <= 1) {
      return first;
    }
    const rest = Array.from({ length: totalPages - 1 }, (_, i) =>
      sdk.availabilityExceptions.query({ ...params, page: i + 2 })
    );
    return Promise.all(rest).then(responses =>
      responses.reduce((all, r) => all.concat(r.data.data), first)
    );
  });

// Read-only, like the dashboard: this page never transitions a transaction.
const loadDataPayloadCreator = ({ search }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { date } = parse(search || '');

  return sdk.ownListings
    .query({ perPage: 25 })
    .then(listingsResponse => {
      const ownListings = listingsResponse.data.data;
      const listing = diaryListing(ownListings);
      const timeZone = listing?.attributes?.availabilityPlan?.timezone || FALLBACK_TIME_ZONE;
      const weekStart = diaryWeekStart(date, timeZone);
      const weekEnd = getStartOf(weekStart, 'day', timeZone, 7, 'days');

      const salesQueryParams = {
        only: 'sale',
        processNames: getSupportedProcessesInfo().map(p => p.name),
        // START,END in ISO 8601: bookings that start in this week.
        bookingStart: `${weekStart.toISOString()},${weekEnd.toISOString()}`,
        include: ['listing', 'customer', 'booking'],
        'fields.transaction': [
          'processName',
          'lastTransition',
          'lastTransitionedAt',
          'transitions',
          'payoutTotal',
          // Pickup address and lesson focus from checkout; only the two parties can read it.
          'protectedData',
        ],
        'fields.listing': ['title'],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
        page: 1,
        perPage: SALES_PAGE_SIZE,
      };

      return Promise.all([
        listing
          ? queryAllExceptions(sdk, { listingId: listing.id, start: weekStart, end: weekEnd })
          : Promise.resolve([]),
        sdk.transactions.query(salesQueryParams),
      ]).then(([exceptions, salesResponse]) => {
        dispatch(addMarketplaceEntities(salesResponse));
        return {
          weekStartId: stringifyDateToISO8601(weekStart, timeZone),
          timeZone,
          ownListings,
          exceptions,
          transactions: salesResponse.data.data,
        };
      });
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const loadDataThunk = createAsyncThunk(
  'InstructorDiaryPage/loadData',
  loadDataPayloadCreator
);

export const loadData = (params, search) => dispatch => dispatch(loadDataThunk({ params, search }));
